import React, { useEffect, useState, useRef } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/storage';
import 'firebase/compat/database';
import '../styles/campaignMap.css';
import i18next from 'i18next';
import { toast } from 'react-toastify';
import { PIN_ICONS } from './CampaignMap';
import MapCollab from './MapCollab';
import { MAX_MAPS } from '../utils/campaignMapPaths';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faEye, faEyeSlash, faPen, faTrash, faUsersSlash, faPencilAlt, faUndo, faEraser } from '@fortawesome/free-solid-svg-icons';

const EMPTY_FORM = { label: '', description: '', icon: 'mapMarkerAlt', hidden: false };

const CampaignMapAdmin = ({ campaignUid, user }) => {
  const [mapsObj, setMapsObj] = useState({});
  const [activeMapId, setActiveMapId] = useState(null); // carte visible par les joueurs
  const [viewMapId, setViewMapId] = useState(null); // carte visualisée par le MJ (locale)
  const [image, setImage] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [collabTool, setCollabTool] = useState('view'); // 'view' | 'draw' | 'erase' (couche joueurs)
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingPinId, setEditingPinId] = useState(null);
  const [pendingPos, setPendingPos] = useState(null);
  const [showPinForm, setShowPinForm] = useState(false);
  const [deleteMapId, setDeleteMapId] = useState(null);
  const imgRef = useRef(null);
  const collabRef = useRef(null);
  const migratedRef = useRef(false);

  useEffect(() => {
    if (!campaignUid) return;
    const ref = firebase.database().ref(`campaignMap/${campaignUid}`);
    ref.on('value', snap => {
      const root = snap.val() || {};
      // Migration de l'ancien format (carte unique à plat) → maps/{id}
      if (root.url && !root.maps && !migratedRef.current) {
        migratedRef.current = true;
        migrateLegacy(root);
        return; // la migration déclenche un nouvel event 'value'
      }
      setMapsObj(root.maps || {});
      setActiveMapId(root.activeMapId || null);
    });
    return () => ref.off();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignUid]);

  const migrateLegacy = async (root) => {
    const mapId = firebase.database().ref().push().key;
    const newMap = {
      url: root.url,
      name: i18next.t('map.defaultMapName'),
      storagePath: `campaignMaps/${campaignUid}.png`,
      createdAt: firebase.database.ServerValue.TIMESTAMP,
    };
    if (root.pins) newMap.pins = root.pins;
    if (root.playerPins) newMap.playerPins = root.playerPins;
    if (root.drawings) newMap.drawings = root.drawings;
    await firebase.database().ref(`campaignMap/${campaignUid}`).update({
      [`maps/${mapId}`]: newMap,
      activeMapId: mapId,
      url: null,
      pins: null,
      playerPins: null,
      drawings: null,
    });
  };

  const mapsList = Object.entries(mapsObj)
    .map(([id, m]) => ({ id, ...m }))
    .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

  // Par défaut, le MJ visualise la carte active ; il peut ensuite en visualiser
  // une autre sans la rendre visible aux joueurs.
  useEffect(() => {
    if (!viewMapId || !mapsObj[viewMapId]) {
      setViewMapId(activeMapId || mapsList[0]?.id || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsObj, activeMapId]);

  // La carte visualisée par le MJ (celle qu'il édite), pas forcément l'active
  const viewed = viewMapId && mapsObj[viewMapId]
    ? { url: mapsObj[viewMapId].url || '', pins: mapsObj[viewMapId].pins, path: `campaignMap/${campaignUid}/maps/${viewMapId}` }
    : null;
  const viewedMapPath = viewed?.path;
  const viewedMapUrl = viewed?.url || '';
  const pins = viewed?.pins ? Object.entries(viewed.pins).map(([id, p]) => ({ id, ...p })) : [];

  const handleUpload = async () => {
    if (!image) return;
    if (image.size > 2000000) {
      toast.error(i18next.t('map.imageTooLarge'));
      return;
    }
    if (mapsList.length >= MAX_MAPS) {
      toast.error(i18next.t('map.maxMaps'));
      return;
    }
    const mapId = firebase.database().ref().push().key;
    const storagePath = `campaignMaps/${campaignUid}/${mapId}.png`;
    const uploadTask = firebase.storage().ref(storagePath).put(image);
    uploadTask.on('state_changed', null, console.error, async () => {
      const url = await firebase.storage().ref(storagePath).getDownloadURL();
      const defaultName = image.name.replace(/\.[^./]+$/, '') || i18next.t('map.defaultMapName');
      const updates = {
        [`maps/${mapId}`]: { url, name: defaultName, storagePath, createdAt: firebase.database.ServerValue.TIMESTAMP },
      };
      if (!activeMapId) updates.activeMapId = mapId; // 1re image → visible d'office
      await firebase.database().ref(`campaignMap/${campaignUid}`).update(updates);
      setImage(null);
      setViewMapId(mapId); // le MJ visualise directement la nouvelle image
      toast.success(i18next.t('map.uploadSuccess'));
    });
  };

  // Rendre la carte visualisée visible par les joueurs
  const publishActive = (mapId) => {
    firebase.database().ref(`campaignMap/${campaignUid}`).update({ activeMapId: mapId });
  };

  const renameMap = (mapId, name) => {
    firebase.database().ref(`campaignMap/${campaignUid}/maps/${mapId}`).update({ name });
  };

  const doDeleteMap = async () => {
    const id = deleteMapId;
    const m = mapsObj[id];
    const remaining = mapsList.filter(x => x.id !== id);
    const updates = { [`maps/${id}`]: null };
    if (activeMapId === id) updates.activeMapId = remaining[0]?.id || null;
    await firebase.database().ref(`campaignMap/${campaignUid}`).update(updates);
    if (m?.storagePath) firebase.storage().ref(m.storagePath).delete().catch(() => {});
    setDeleteMapId(null);
  };

  const handleMapClick = (e) => {
    if (!editMode) return;
    if (editingPinId) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingPos({ x, y });
    setForm(EMPTY_FORM);
    setShowPinForm(true);
  };

  const savePin = async () => {
    if (!form.label.trim()) {
      toast.error(i18next.t('map.labelRequired'));
      return;
    }
    const ref = firebase.database().ref(`${viewedMapPath}/pins`);
    if (editingPinId) {
      await ref.child(editingPinId).update({ label: form.label, description: form.description, icon: form.icon, hidden: form.hidden });
    } else {
      await ref.push({ x: pendingPos.x, y: pendingPos.y, label: form.label, description: form.description, icon: form.icon, hidden: form.hidden });
    }
    setShowPinForm(false);
    setEditingPinId(null);
    setPendingPos(null);
    setForm(EMPTY_FORM);
  };

  const deletePin = async (id) => {
    await firebase.database().ref(`${viewedMapPath}/pins/${id}`).remove();
    setShowPinForm(false);
    setEditingPinId(null);
  };

  const togglePinVisibility = async (pin) => {
    await firebase.database().ref(`${viewedMapPath}/pins/${pin.id}`).update({ hidden: !pin.hidden });
  };

  const openEditPin = (pin) => {
    setEditingPinId(pin.id);
    setForm({ label: pin.label, description: pin.description || '', icon: pin.icon || 'mapMarkerAlt', hidden: pin.hidden || false });
    setShowPinForm(true);
  };

  const cancelForm = () => {
    setShowPinForm(false);
    setEditingPinId(null);
    setPendingPos(null);
    setForm(EMPTY_FORM);
  };

  return (
    <div className="mapAdminContainer">
      <div className="mapAdminToolbar">
        <div className="mapUploadRow">
          <label className={`mapFileLabel${mapsList.length >= MAX_MAPS ? ' disabled' : ''}`}>
            <input
              type="file"
              accept="image/*"
              className="inputfile"
              disabled={mapsList.length >= MAX_MAPS}
              onChange={e => setImage(e.target.files[0])}
            />
            <span>{image ? image.name : i18next.t('map.chooseImage')}</span>
          </label>
          {image && (
            <button className="mapUploadBtn" onClick={handleUpload}>
              {i18next.t('map.upload')}
            </button>
          )}
          <span className="mapCountHint">{mapsList.length}/{MAX_MAPS}</span>
        </div>
        {viewedMapUrl && viewMapId !== activeMapId && (
          <button
            className="mapEditToggle mapPublishBtn"
            onClick={() => publishActive(viewMapId)}
            title={i18next.t('map.makeVisible')}
          >
            <FontAwesomeIcon icon={faEye} /> {i18next.t('map.makeVisible')}
          </button>
        )}
        {viewedMapUrl && viewMapId === activeMapId && (
          <span className="mapVisibleBadge">
            <FontAwesomeIcon icon={faEye} /> {i18next.t('map.visibleToPlayers')}
          </span>
        )}
        {viewedMapUrl && (
          <div className="mapToolModes">
            <button
              className={`mapCollabTool${editMode ? ' active' : ''}`}
              onClick={() => { setEditMode(!editMode); setCollabTool('view'); cancelForm(); }}
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} /> {i18next.t('map.toolAddPin')}
            </button>
            <button
              className={`mapCollabTool${collabTool === 'draw' ? ' active' : ''}`}
              onClick={() => { setCollabTool(t => (t === 'draw' ? 'view' : 'draw')); setEditMode(false); cancelForm(); }}
            >
              <FontAwesomeIcon icon={faPencilAlt} /> {i18next.t('map.toolDraw')}
            </button>
            <button
              className={`mapCollabTool${collabTool === 'erase' ? ' active' : ''}`}
              onClick={() => { setCollabTool(t => (t === 'erase' ? 'view' : 'erase')); setEditMode(false); cancelForm(); }}
            >
              <FontAwesomeIcon icon={faEraser} /> {i18next.t('map.toolErase')}
            </button>
          </div>
        )}
        {viewedMapUrl && collabTool === 'draw' && (
          <div className="mapToolContext">
            <button className="mapCollabTool" onClick={() => collabRef.current?.undo()}>
              <FontAwesomeIcon icon={faUndo} /> {i18next.t('map.undo')}
            </button>
            <button className="mapCollabTool" onClick={() => collabRef.current?.clearMine()}>
              <FontAwesomeIcon icon={faTrash} /> {i18next.t('map.clearMine')}
            </button>
          </div>
        )}
        {viewedMapUrl && (
          <button
            className="mapEditToggle mapClearAllBtn"
            onClick={() => collabRef.current?.clearAll()}
            title={i18next.t('map.clearAll')}
          >
            <FontAwesomeIcon icon={faUsersSlash} /> {i18next.t('map.clearAll')}
          </button>
        )}
      </div>

      {mapsList.length > 0 && (
        <div className="mapGallery">
          {mapsList.map(m => (
            <div key={m.id} className={`mapGalleryItem${m.id === viewMapId ? ' viewing' : ''}`}>
              <button className="mapGalleryThumb" onClick={() => setViewMapId(m.id)} title={i18next.t('map.viewImage')}>
                <img src={m.url} alt={m.name} />
                {m.id === activeMapId && (
                  <span className="mapGalleryActiveBadge" title={i18next.t('map.visibleToPlayers')}>
                    <FontAwesomeIcon icon={faEye} />
                  </span>
                )}
              </button>
              <input
                className="mapGalleryName"
                type="text"
                value={m.name || ''}
                placeholder={i18next.t('map.mapNamePlaceholder')}
                onChange={e => renameMap(m.id, e.target.value)}
              />
              <button className="mapGalleryDelete" onClick={() => setDeleteMapId(m.id)} title={i18next.t('map.deleteMap')}>
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
        </div>
      )}

      {editMode && (
        <p className="mapEditHint">{i18next.t('map.clickToAdd')}</p>
      )}
      {collabTool === 'draw' && (
        <p className="mapEditHint">{i18next.t('map.drawHint')}</p>
      )}
      {collabTool === 'erase' && (
        <p className="mapEditHint">{i18next.t('map.eraseHint')}</p>
      )}

      {viewedMapUrl && (
        <div className="mapAdminLayout">
          <div className="mapWrapper">
            <div className="mapImageContainer" onClick={handleMapClick}>
              <img ref={imgRef} src={viewedMapUrl} alt="carte" className="mapImage" draggable={false} />
              {pins.map(pin => (
                <button
                  key={pin.id}
                  className={`mapPin${pin.hidden ? ' mapPinHidden' : ''}${editMode ? ' mapPinEditable' : ''}`}
                  style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                  onClick={(e) => { e.stopPropagation(); if (editMode) openEditPin(pin); }}
                  title={pin.label}
                >
                  <FontAwesomeIcon icon={PIN_ICONS.find(p => p.key === pin.icon)?.icon || faMapMarkerAlt} />
                </button>
              ))}
              {pendingPos && !editingPinId && (
                <div className="mapPinPending" style={{ left: `${pendingPos.x}%`, top: `${pendingPos.y}%` }}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                </div>
              )}
              <MapCollab
                ref={collabRef}
                mapPath={viewedMapPath}
                user={user}
                toolMode={collabTool}
                isDm
              />
            </div>
          </div>

          {pins.length > 0 && (
            <div className="mapPinList">
              <h3 className="mapPinListTitle">{i18next.t('map.pins')}</h3>
              {pins.map(pin => (
                <div key={pin.id} className={`mapPinListItem${pin.hidden ? ' mapPinListHidden' : ''}`}>
                  <span className="mapPinListIcon">
                    <FontAwesomeIcon icon={PIN_ICONS.find(p => p.key === pin.icon)?.icon || faMapMarkerAlt} />
                  </span>
                  <span className="mapPinListLabel">{pin.label}</span>
                  <div className="mapPinListActions">
                    <button onClick={() => togglePinVisibility(pin)} title={pin.hidden ? i18next.t('map.reveal') : i18next.t('map.hide')}>
                      <FontAwesomeIcon icon={pin.hidden ? faEye : faEyeSlash} />
                    </button>
                    <button onClick={() => openEditPin(pin)} title={i18next.t('map.edit')}>
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button onClick={() => deletePin(pin.id)} title={i18next.t('map.delete')}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showPinForm && (
        <div className="mapPinFormOverlay" onClick={cancelForm}>
          <div className="mapPinForm" onClick={e => e.stopPropagation()}>
            <h3>{editingPinId ? i18next.t('map.editPin') : i18next.t('map.addPin')}</h3>
            <div className="mapPinIconPicker">
              {PIN_ICONS.map(({ key, icon, label }) => (
                <button
                  key={key}
                  className={`mapPinIconOption${form.icon === key ? ' selected' : ''}`}
                  onClick={() => setForm({ ...form, icon: key })}
                  title={label}
                >
                  <FontAwesomeIcon icon={icon} />
                </button>
              ))}
            </div>
            <input
              className="mapPinInput"
              type="text"
              placeholder={i18next.t('map.labelPlaceholder')}
              value={form.label}
              onChange={e => setForm({ ...form, label: e.target.value })}
              autoFocus
            />
            <textarea
              className="mapPinTextarea"
              placeholder={i18next.t('map.descriptionPlaceholder')}
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={3}
            />
            <label className="mapPinHiddenToggle">
              <input
                type="checkbox"
                checked={form.hidden}
                onChange={e => setForm({ ...form, hidden: e.target.checked })}
              />
              {i18next.t('map.hidePin')}
            </label>
            <div className="mapPinFormActions">
              {editingPinId && (
                <button className="mapPinDeleteBtn" onClick={() => deletePin(editingPinId)}>
                  {i18next.t('map.delete')}
                </button>
              )}
              <button className="mapPinCancelBtn" onClick={cancelForm}>{i18next.t('cancel')}</button>
              <button className="mapPinSaveBtn" onClick={savePin}>{i18next.t('save')}</button>
            </div>
          </div>
        </div>
      )}

      {deleteMapId && (
        <div className="mapPinFormOverlay" onClick={() => setDeleteMapId(null)}>
          <div className="mapPinForm mapConfirm" onClick={e => e.stopPropagation()}>
            <h3>{i18next.t('map.deleteMap')}</h3>
            <p className="mapConfirmText">{i18next.t('map.deleteMapConfirm')}</p>
            <div className="mapPinFormActions">
              <button className="mapPinCancelBtn" onClick={() => setDeleteMapId(null)}>{i18next.t('cancel')}</button>
              <button className="mapPinDeleteBtn mapConfirmBtn" onClick={doDeleteMap}>{i18next.t('map.delete')}</button>
            </div>
          </div>
        </div>
      )}

      {mapsList.length === 0 && !image && (
        <div className="mapEmpty">
          <span>{i18next.t('map.noMapAdmin')}</span>
        </div>
      )}
    </div>
  );
};

export default CampaignMapAdmin;
